import { useEffect, useState } from "react";
import type {
  AdminContactImportSource,
  ContactSyncSource,
  ScheduledEvent,
} from "@tih/shared";
import {
  ApiError,
  adminFetchEvents,
  adminImportContacts,
  adminSyncContacts,
} from "../../../api/client";

const IMPORT_SOURCES: { value: AdminContactImportSource; label: string }[] = [
  { value: "attendees", label: "Event attendees" },
  { value: "reservations", label: "Reservations / RSVPs" },
  { value: "all", label: "Attendees and reservations" },
];

function eventLabel(event: ScheduledEvent): string {
  const when = new Date(event.startsAt).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });
  return event.title?.trim() ? `${event.title} - ${when}` : when;
}

function parseTagInput(value: string): string[] {
  return value
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

export function AdminSettingsAddressBookPage() {
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [importSource, setImportSource] = useState<AdminContactImportSource>("all");
  const [importEventId, setImportEventId] = useState<number | null>(null);
  const [importTags, setImportTags] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const ev = await adminFetchEvents();
        if (!cancelled) setEvents(ev);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Could not load events");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function runImport() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminImportContacts({
        source: importSource,
        eventId: importEventId ?? undefined,
        tags: parseTagInput(importTags),
      });
      setSuccess(`Import: ${result.created} added, ${result.updated} updated.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not import");
    } finally {
      setBusy(false);
    }
  }

  async function runSync(source: ContactSyncSource) {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminSyncContacts({ source });
      const label =
        source === "subscribers"
          ? "subscribers"
          : source === "users"
            ? "registered users"
            : source === "attendees"
              ? "attendees"
              : "all sources";
      setSuccess(
        `Sync ${label}: ${result.created} created, ${result.updated} updated` +
          (result.linked ? `, ${result.linked} linked` : "") +
          (result.skipped ? `, ${result.skipped} skipped` : "") +
          ".",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sync");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div style={{ opacity: 0.5 }}>Loading…</div>;
  }

  return (
    <div>
      <section className="admin-card" style={{ marginBottom: 24 }}>
        <h2 className="admin-card-title">Sync address book</h2>
        <p className="admin-card-sub">
          Match by phone (E.164) first, else email. Creates missing rows, fills
          missing phone/email, links registered users, pulls attendees / RSVPs,
          and never wipes tags. Email-only people are included. Only SMS-quality
          mobiles are stored; invalid numbers stay skipped (fix in People →
          Address book via Needs phone fix).
        </p>
        <p className="admin-card-sub">
          New attendees, check-ins, and RSVPs join the book automatically. Sync
          attendees is for backfill / catch-up.
        </p>
        <div
          className="admin-blog-form-actions"
          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => void runSync("subscribers")}
          >
            Sync subscribers
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => void runSync("users")}
          >
            Sync registered users
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => void runSync("attendees")}
          >
            Sync attendees
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => void runSync("all")}
          >
            Sync all
          </button>
        </div>
      </section>

      <section className="admin-card">
        <h2 className="admin-card-title">Pull from attendees / RSVPs</h2>
        <p className="admin-card-sub">
          Copies names, mobiles, and emails already captured on attendee and RSVP
          records (email-only rows included). Existing contacts keep their name and
          tags.
        </p>
        <div className="admin-blog-form">
          <div className="admin-blog-form-fields">
            <label>
              <span>Source</span>
              <select
                value={importSource}
                disabled={busy}
                onChange={(e) =>
                  setImportSource(e.target.value as AdminContactImportSource)
                }
              >
                {IMPORT_SOURCES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Event</span>
              <select
                value={importEventId ?? ""}
                disabled={busy}
                onChange={(e) => setImportEventId(Number(e.target.value) || null)}
              >
                <option value="">Every event</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {eventLabel(event)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Tags for imported contacts (optional)</span>
              <input
                value={importTags}
                disabled={busy}
                onChange={(e) => setImportTags(e.target.value)}
                placeholder="guests"
              />
            </label>
          </div>
          <div className="admin-blog-form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void runImport()}
            >
              Import
            </button>
          </div>
        </div>
        <p className="admin-card-foot">
          Day-to-day contact CRUD, paste import, and merge stay on{" "}
          <a href="/admin/people/address-book">People → Address book</a>.
        </p>
      </section>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12 }}>
          {success}
        </div>
      )}
    </div>
  );
}
